import './DBM.scss';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, Tabs } from '@mui/material';
import DbmHistory from '../../components/DbmHistory/DbmHistory';
import DbmReport from '../../components/DbmReport/DbmReport';
import Loader from '../../components/Loader/Loader';
import { SearchBar } from '../../components/SearchBar/SearchBar';
import { pushSiteInfo, registerPageInfo } from '../../helpers/analyticsHelper';
import {
  generatePrevNMonths,
  getFormattedMonth
} from '../../helpers/dateHelpers';
import { CollectiveAgreementInteface } from '../../models/CollectiveAgreementInteface';
import { FreezePeriodInterface } from '../../models/FreezePeriodInterface';
import { SearchStateInterface } from '../../models/SearchInterface';
import { useAuth } from '../../providers/Auth';
import { useCollectiveAgreement } from '../../providers/CollectiveAgreement';
import { useDBM } from '../../providers/DBM';
import { useFreezePeriod } from '../../providers/FreezePeriod';
import { useNotifications } from '../../providers/Notifications';
import { useProfile } from '../../providers/Profile';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {children}
    </div>
  );
}

const generateYearDropdown = (currentYear: number): any[] => {
  const options: any[] = [];
  const yearDropdownOptionsCount = 3;
  for (let i = 0; i < yearDropdownOptionsCount; i++) {
    options.push(getYearDropdownOptions(currentYear - i));
  }

  return options;
};

const getYearDropdownOptions = (currentYear) => {
  const toDisplay = `MAY ${currentYear + 1}`;
  const fromDisplay = `JUN ${currentYear}`;
  const toValue = `5 ${currentYear + 1}`;
  const fromValue = `6 ${currentYear}`;
  return {
    dateRangeDisplay: `${fromDisplay} - ${toDisplay}`,
    dateRangeValue: `${fromValue} - ${toValue}`
  };
};

export default function DBM() {
  const user = useAuth();
  const collectiveAgreementContext = useCollectiveAgreement();
  const dbmContext = useDBM();
  const freezePeriodContext = useFreezePeriod();
  const notificationsContext = useNotifications();
  const profileContext = useProfile();
  const { t } = useTranslation();

  const [currentPairingMonthData, setCurrentPairingMonthData] =
    useState<FreezePeriodInterface>();
  const [dbmYearlyEndYear, setDbmYearlyEndYear] = useState(
    String(new Date().getFullYear())
  );
  const [dnRates, setDNRates] = useState({} as CollectiveAgreementInteface);
  const [searchState, setSearchState] = useState<SearchStateInterface>({
    selectedDate: '',
    selectedBase: '',
    selectedEquipment: '',
    selectedPosition: ''
  });
  const [defaultSearchState, setDefaultSearchState] =
    useState<SearchStateInterface>({
      selectedDate: '',
      selectedBase: '',
      selectedEquipment: '',
      selectedPosition: ''
    });
  const [searchVisibility, setSearchVisibility] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [dateOptions, setDateOptions] = useState<string[]>([]);

  let yearDropdownOptions: any = [];
  let currentYear = new Date().getFullYear();

  yearDropdownOptions = generateYearDropdown(currentYear);

  useEffect(() => {
    if (
      !freezePeriodContext.freezePeriodData.pairingFreeze ||
      freezePeriodContext.loading
    )
      return;
    setCurrentPairingMonthData({
      ...freezePeriodContext.freezePeriodData.pairingFreeze.filter(
        (val) => val
      )[0]
    });
    notificationsContext.getNotifications();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freezePeriodContext]);

  useEffect(() => {
    setSearchState({
      ...searchState,
      selectedDate: getSelectedDate()
    });
    setDefaultSearchState({
      ...defaultSearchState,
      selectedDate: getSelectedDate()
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex]);

  useEffect(() => {
    if (!searchState?.selectedBase) return;
    if (tabIndex) {
      setDbmYearlyEndYear(
        searchState.selectedDate.split('- ')[1].split(' ')[1].slice(2)
      );
      dbmContext.getDBMYearlyReports({
        ...searchState,
        selectedDate: getYearDropdownValueFromDisplay()
      });
    } else {
      dbmContext.getDBMMonthlyReports({ ...searchState });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState]);

  useEffect(() => {
    if (!profileContext.profile.results || !currentPairingMonthData) return;

    setSearchState({
      selectedDate: getSelectedDate(),
      selectedBase: profileContext.profile.results?.baseLocation,
      selectedEquipment: 'All',
      selectedPosition: profileContext.profile.results?.position
    });

    setDefaultSearchState({
      selectedDate: getSelectedDate(),
      selectedBase: profileContext.profile.results?.baseLocation,
      selectedEquipment: 'All',
      selectedPosition: profileContext.profile.results?.position
    });
    setDateOptions(getMonthDropDownOptions());
    /* to send read receipts for the dbm notifications */

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileContext.profile.results, currentPairingMonthData]);

  useEffect(() => {
    if (!notificationsContext?.notifications?.result) return;

    if (notificationsContext?.notifications?.result?.length >= 1) {
      notificationsContext.notifications.result.forEach((msg) => {
        if (msg.module === 'dbm.updated') {
          notificationsContext.updateNotificationViewed(
            msg.year,
            msg.month,
            'dbm.updated',
            msg.notificationId
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsContext.notifications]);

  useEffect(() => {
    if (!collectiveAgreementContext.collectiveAgreement) return;
    setDNRates(
      collectiveAgreementContext.collectiveAgreement as CollectiveAgreementInteface
    );
  }, [collectiveAgreementContext.collectiveAgreement]);

  useEffect(() => {
    if (user.loading || !profileContext.profile.results) return;
    pushSiteInfo(user.user, profileContext.profile);
    registerPageInfo('my access app|planning & bidding|dbm report', {
      level1: 'my access app',
      level2: 'planning & bidding',
      level3: 'dbm report',
      level4: null
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profileContext.profile.results]);

  const getMonthDropDownOptions = () => {
    if (!currentPairingMonthData)
      return generatePrevNMonths(13, 3).map((f) => `${f.month} ${f.year}`);
    return generatePrevNMonths(
      13,
      2,
      new Date(
        currentPairingMonthData.pairingYear,
        currentPairingMonthData.pairingMonth - 1
      )
    ).map((f) => `${f.month} ${f.year}`);
  };

  const getSelectedDate = () => {
    let selectedDate = '';
    if (!tabIndex) {
      if (currentPairingMonthData) {
        selectedDate = getFormattedMonth(
          currentPairingMonthData.pairingMonth,
          currentPairingMonthData.pairingYear
        );
      }
      if (currentPairingMonthData && !currentPairingMonthData.dbmPosted) {
        selectedDate = `${generatePrevNMonths(13, 2, new Date(currentPairingMonthData.pairingYear, currentPairingMonthData.pairingMonth - 1))[1].month} ${generatePrevNMonths(13, 2, new Date(currentPairingMonthData.pairingYear, currentPairingMonthData.pairingMonth - 1))[1].year}`;
      }
    } else {
      selectedDate = yearDropdownOptions[0].dateRangeDisplay;
    }
    return selectedDate;
  };

  const getYearDropDownOptions = () => {
    return yearDropdownOptions.map((o) => {
      return o.dateRangeDisplay;
    });
  };

  const getYearDropdownValueFromDisplay = () => {
    const index = yearDropdownOptions.findIndex(
      (o) => o.dateRangeDisplay === searchState.selectedDate
    );
    return yearDropdownOptions[index].dateRangeValue;
  };

  const handleTabChange = (index) => {
    setTabIndex(index);
  };

  const toggleNewSearch = () => {
    setSearchVisibility((prev) => !prev);
  };

  return (
    <div className="dbm-report-container">
      {profileContext.loading || dbmContext.loading ? (
        <Loader />
      ) : (
        <>
          <div className="dbm-header-container">
            <div className="header-grey header-content">
              <h1>{t('dbm.title')}</h1>
              {!searchVisibility ? (
                <>
                  <div className="sub-title">
                    <span>{searchState.selectedDate}</span>
                    {searchState.selectedBase.toLowerCase() === 'all' ? (
                      <span>{t('SearchBar.allbases')}</span>
                    ) : (
                      <span>{searchState.selectedBase}</span>
                    )}
                    {searchState.selectedEquipment.toLowerCase() === 'all' ? (
                      <span>{t('SearchBar.allequip')}</span>
                    ) : (
                      <span>{searchState.selectedEquipment}</span>
                    )}
                    {searchState.selectedPosition.toLowerCase() === 'all' ? (
                      <span>{t('SearchBar.allpos')}</span>
                    ) : (
                      <span>{searchState.selectedPosition}</span>
                    )}
                  </div>
                  <span
                    className="new-search-link link-states"
                    onClick={toggleNewSearch}
                  >
                    <i className="icon-manage_accounts_FILL0_wght400_GRAD0_opsz48-1-1 change-position-icon" />
                    {t('pairingPackage.newSearch')}
                  </span>
                </>
              ) : (
                <div className="new-search-form">
                  <SearchBar
                    dateOptions={
                      !tabIndex ? dateOptions : getYearDropDownOptions()
                    }
                    includeDateField
                    searchState={searchState}
                    defaultSearchState={defaultSearchState}
                    setSearchState={setSearchState}
                    setSearchVisibility={setSearchVisibility}
                    selectDateLabel={tabIndex ? 'Year' : undefined}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="dbm-container">
            <div className="content-switcher dbm-content">
              <div className="history-selector">
                <Tabs
                  className="dbm-tabs"
                  value={tabIndex}
                  onChange={(e, index) => handleTabChange(index)}
                  TabIndicatorProps={{ style: { display: 'none' } }}
                >
                  <Tab label={t('dbm.tabMonth')} disableRipple />
                  <Tab label={t('dbm.tabYear')} disableRipple />
                </Tabs>
              </div>
            </div>
            <div className="multisort-instruction dbm-content">
              {t('dbm.multiSortInstruction')}
            </div>
            <TabPanel className="dbm-content" value={tabIndex} index={0}>
              <DbmReport dnRates={dnRates} />
            </TabPanel>
            <TabPanel className="dbm-content" value={tabIndex} index={1}>
              <DbmHistory to={dbmYearlyEndYear} />
            </TabPanel>
          </div>
        </>
      )}
    </div>
  );
}
